import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { authTelegram } from './api';
import { useAppStore } from './store';
import { useHaptic } from './hooks/useHaptic';
import { useWebappUpdateProbe } from './hooks/useWebappUpdateProbe';
import { normalizeTelegramId } from './adminAccess';
import { authCrmHintsFromApi } from './crmAccess';
import BottomNav from './components/BottomNav';
import CatalogPage from './pages/CatalogPage';
import CartPage from './pages/CartPage';
import OrdersPage from './pages/OrdersPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import ProductGroupPage from './pages/ProductGroupPage';
import SmartHomePage from './pages/SmartHomePage';
import CatalogItemPage from './pages/CatalogItemPage';
import MeasurementsPage from './pages/MeasurementsPage';
import AppErrorBoundary from './components/AppErrorBoundary';

const springEase: [number, number, number, number] = [0.16, 1, 0.3, 1];

function AnimatedRoutes() {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const transition = reduceMotion ? { duration: 0 } : { duration: 0.28, ease: springEase };
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : -8 }}
        transition={transition}
        className="flex-1 route-shell-gpu"
      >
        <Routes location={location}>
          <Route path="/" element={<Navigate to="/catalog" replace />} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/product/:id" element={<Navigate to="/catalog" replace />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/measurements" element={<MeasurementsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/product-group" element={<ProductGroupPage />} />
          <Route path="/catalog-item" element={<CatalogItemPage />} />
          <Route path="/smart-home" element={<SmartHomePage />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

export default function App() {
  const { setAuth, setDiscount } = useAppStore();
  const haptic = useHaptic();
  const { updateAvailable, reload } = useWebappUpdateProbe();

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready?.();
      tg.expand?.();
      const initData = tg.initData;
      const initDataUnsafe = tg.initDataUnsafe;
      const photoUrl = initDataUnsafe?.user?.photo_url || null;
      const tgFromInit = normalizeTelegramId(initDataUnsafe?.user?.id);
      if (initData) {
        authTelegram(initData)
          .then((data: any) => {
            const tid = normalizeTelegramId(data.telegram_id) ?? tgFromInit;
            setAuth({
              token: data.access_token,
              userId: data.user_id,
              role: data.role,
              firstName: data.first_name,
              photoUrl: photoUrl,
              telegramId: tid,
              ...authCrmHintsFromApi(data),
            });
            setDiscount(
              Number(
                data.effective_discount_pct ??
                  data.partner_discount_pct ??
                  0,
              ),
            );
          })
          .catch(console.error);
      }
    }
  }, []);

  return (
    <BrowserRouter>
      <AppErrorBoundary>
        {updateAvailable && (
          <div
            className="fixed left-3 right-3 z-[55] rounded-2xl px-3.5 py-2.5 flex items-center gap-3 shadow-lg backdrop-blur-xl"
            style={{
              bottom: 'calc(var(--app-bottom-nav-offset) + 0.5rem)',
              background: 'rgba(10,25,47,0.92)',
              border: '1px solid rgba(129,199,132,0.45)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35), 0 0 24px rgba(129,199,132,0.12)',
            }}
            role="status"
          >
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold" style={{ color: '#E8FFF0' }}>
                Доступна новая версия магазина
              </p>
              <p className="text-[10px] mt-0.5 leading-snug" style={{ color: 'var(--text-3)' }}>
                На Vercel уже свежий интерфейс — обновите страницу, чтобы увидеть изменения сразу.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                haptic.selection();
                reload();
              }}
              className="press shrink-0 px-3.5 py-2 rounded-xl text-xs font-bold text-white"
              style={{
                background: 'linear-gradient(135deg,#2d8f4e,#1e6b3a)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              Обновить
            </button>
          </div>
        )}
        <div className="flex flex-col min-h-screen" style={{ paddingBottom: 'var(--app-scroll-pad-bottom)' }}>
          <AnimatedRoutes />
          <BottomNav />
        </div>
      </AppErrorBoundary>
    </BrowserRouter>
  );
}
